import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { seriesFormatApi, SeriesFormat, CreateSeriesFormatRequest, workflowApi, strategyApi } from '../services/api';
import { PageHeader, useConfirm, EmptyState, RelatedLinks, useFocusParam } from '../components/ui';
import { routeById } from '../navigation/routes';
import { FlowStepBar, useLinkedInFlow } from '../linkedin';

const SeriesFormatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { refresh } = useLinkedInFlow();
  const [seriesFormats, setSeriesFormats] = useState<SeriesFormat[]>([]);
  useFocusParam('id', seriesFormats.length > 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateSeriesFormatRequest>({
    name: '',
    title: '',
    description: '',
    footer: '',
    schedule: '',
  });

  // AI Suggestion state
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiCurrentStep, setAiCurrentStep] = useState(0);
  const [aiSuggestions, setAiSuggestions] = useState<CreateSeriesFormatRequest[]>([]);
  const [aiRawResults, setAiRawResults] = useState<any[]>([]);
  const [aiInputs, setAiInputs] = useState({
    industry: '',
    target_audience: '',
    strategy_context: '',
  });

  useEffect(() => {
    loadSeriesFormats();
  }, []);

  const loadSeriesFormats = async () => {
    try {
      setLoading(true);
      const data = await seriesFormatApi.getAll();
      setSeriesFormats(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Serienformate');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await seriesFormatApi.update(editingId, formData);
      } else {
        await seriesFormatApi.create(formData);
      }
      await loadSeriesFormats();
      refresh();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (format: SeriesFormat) => {
    setEditingId(format.id);
    setFormData({
      name: format.name,
      title: format.title,
      description: format.description || '',
      footer: format.footer || '',
      schedule: format.schedule || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Serienformat löschen?',
      message: 'Zugeordnete Beitragsideen bleiben erhalten, verlieren aber ihr Format.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await seriesFormatApi.delete(id);
      await loadSeriesFormats();
      refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      description: '',
      footer: '',
      schedule: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleRunAiSuggestion = async () => {
    if (!aiInputs.industry || !aiInputs.target_audience) {
      setError('Bitte Branche und Zielgruppe angeben');
      return;
    }

    setAiRunning(true);
    setAiSuggestions([]);
    setAiRawResults([]);
    setAiCurrentStep(0);
    setError('');

    try {
      const workflows = await workflowApi.getWorkflows();
      const serienWorkflow = workflows.find((w: any) =>
        w.name.includes('Phase 2: KI-gestuetzte Serienformate')
      );

      if (!serienWorkflow) {
        setError('Serienformat-Workflow nicht gefunden. Bitte unter Workflows die Schaltflaeche "Vorlagen laden" verwenden.');
        setAiRunning(false);
        return;
      }

      const steps = JSON.parse(serienWorkflow.steps);
      const variables: Record<string, string> = { ...aiInputs };
      const results: any[] = [];

      for (let i = 0; i < steps.length; i++) {
        setAiCurrentStep(i + 1);
        const result = await workflowApi.executeWorkflowStep(serienWorkflow.id, {
          variables,
          stepIndex: i,
        });
        results.push(result.step);
        variables[`step_${steps[i].id}_result`] = result.step.content;
      }

      setAiRawResults(results);

      // Try to parse JSON from the last step
      const lastContent = results[results.length - 1]?.content || '';
      const jsonMatch = lastContent.match(/```json\s*([\s\S]*?)```/) || lastContent.match(/\[\s*\{[\s\S]*\}\s*\]/);
      if (jsonMatch) {
        const jsonStr = jsonMatch[1] || jsonMatch[0];
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed)) {
          setAiSuggestions(parsed.map((item: any) => ({
            name: item.name || '',
            title: item.title || '',
            description: item.description || '',
            footer: item.footer || '',
            schedule: item.schedule || '',
          })));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Ausfuehren des Workflows');
    } finally {
      setAiRunning(false);
    }
  };

  const handleSaveAiSuggestion = async (suggestion: CreateSeriesFormatRequest) => {
    try {
      await seriesFormatApi.create(suggestion);
      await loadSeriesFormats();
      refresh();
      setAiSuggestions(prev => prev.filter(s => s.name !== suggestion.name));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleSaveAllAiSuggestions = async () => {
    try {
      for (const suggestion of aiSuggestions) {
        await seriesFormatApi.create(suggestion);
      }
      await loadSeriesFormats();
      refresh();
      setAiSuggestions([]);
      setShowAiSuggestion(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  // Load strategy data for AI inputs
  const loadStrategyContext = async () => {
    try {
      const strategies = await strategyApi.getAll();
      if (strategies.length > 0) {
        const latest = strategies[0];
        setAiInputs({
          industry: latest.industry,
          target_audience: latest.targetAudience,
          strategy_context: latest.icp || latest.rawAnalysis || '',
        });
      }
    } catch {}
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><span className="loading-text">Laden...</span></div>;
  }

  return (
    <div className="page-container">
      <FlowStepBar stepId="series" />
      <PageHeader
        title="Serienformate"
        subtitle={routeById('li-series')?.description}
        actions={(
          <>
            <button
              onClick={() => { setShowAiSuggestion(!showAiSuggestion); if (!showAiSuggestion) loadStrategyContext(); }}
              className="btn btn-success"
            >
              {showAiSuggestion ? 'KI schliessen' : 'KI-Vorschlaege generieren'}
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn btn-primary"
            >
              {showForm ? 'Abbrechen' : '+ Neues Serienformat'}
            </button>
          </>
        )}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* AI Suggestion Section */}
      {showAiSuggestion && (
        <div className="form-section" style={{ borderLeft: '4px solid var(--sec-success)' }}>
          <h2 className="form-section-title">KI-gestuetzte Serienformate ermitteln</h2>
          <p style={{ color: 'var(--sec-fg-muted)', fontSize: '14px', marginBottom: '16px' }}>
            Lasse die KI passende Serienformate vorschlagen basierend auf deiner Zielgruppe und Branche.
            Der Workflow nutzt Perplexity, ChatGPT und Claude in 3 Schritten.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">Branche *</label>
              <input
                type="text"
                value={aiInputs.industry}
                onChange={(e) => setAiInputs({ ...aiInputs, industry: e.target.value })}
                placeholder="z.B. IT-Sicherheit, SaaS"
                className="form-input"
                disabled={aiRunning}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Zielgruppe *</label>
              <input
                type="text"
                value={aiInputs.target_audience}
                onChange={(e) => setAiInputs({ ...aiInputs, target_audience: e.target.value })}
                placeholder="z.B. IT-Leiter und CISOs"
                className="form-input"
                disabled={aiRunning}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Strategie-Kontext (optional - aus Phase 1)</label>
            <textarea
              value={aiInputs.strategy_context}
              onChange={(e) => setAiInputs({ ...aiInputs, strategy_context: e.target.value })}
              placeholder="ICP, Pain Points oder andere strategische Informationen aus Phase 1..."
              rows={4}
              className="form-textarea"
              disabled={aiRunning}
            />
          </div>

          <button
            onClick={handleRunAiSuggestion}
            disabled={aiRunning}
            className="btn btn-success"
            style={{ marginBottom: '16px' }}
          >
            {aiRunning ? `Schritt ${aiCurrentStep}/3 wird ausgefuehrt...` : 'Serienformate vorschlagen lassen'}
          </button>

          {/* Progress Steps */}
          {(aiRunning || aiRawResults.length > 0) && (
            <div style={{ marginTop: '16px' }}>
              {['Serienformate recherchieren (Perplexity)', '5 Formate vorschlagen (ChatGPT)', 'Formate verfeinern & finalisieren (Claude)'].map((stepName, idx) => (
                <div key={idx} style={{
                  padding: '10px 14px', marginBottom: '6px', borderRadius: '8px',
                  backgroundColor: aiRawResults[idx] ? 'color-mix(in srgb, var(--sec-success) 12%, var(--sec-surface))' : aiCurrentStep === idx + 1 && aiRunning ? 'color-mix(in srgb, var(--sec-warning) 14%, var(--sec-surface))' : 'var(--sec-surface-alt)',
                  border: `1px solid ${aiRawResults[idx] ? 'color-mix(in srgb, var(--sec-success) 30%, var(--sec-surface))' : aiCurrentStep === idx + 1 && aiRunning ? 'color-mix(in srgb, var(--sec-warning) 35%, var(--sec-surface))' : 'var(--sec-border)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: '22px', height: '22px', borderRadius: '50%',
                      backgroundColor: aiRawResults[idx] ? 'var(--sec-success)' : aiCurrentStep === idx + 1 && aiRunning ? 'var(--sec-warning)' : 'var(--sec-fg-subtle)',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 'bold',
                    }}>
                      {aiRawResults[idx] ? '\u2713' : idx + 1}
                    </span>
                    <strong style={{ fontSize: '13px' }}>{stepName}</strong>
                    {aiCurrentStep === idx + 1 && aiRunning && (
                      <span style={{ fontSize: '11px', color: 'var(--sec-warning)' }}>Wird ausgefuehrt...</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* AI Suggestions Cards */}
          {aiSuggestions.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>
                  {aiSuggestions.length} Serienformat-Vorschlaege
                </h3>
                <button onClick={handleSaveAllAiSuggestions} className="btn btn-success btn-sm">
                  Alle speichern
                </button>
              </div>
              <div className="grid-cards-2">
                {aiSuggestions.map((suggestion, idx) => (
                  <div key={idx} className="card" style={{ borderLeft: '4px solid var(--sec-success)' }}>
                    <h3 className="card-title">{suggestion.name}</h3>
                    <p className="card-description"><strong>{suggestion.title}</strong></p>
                    {suggestion.description && <p className="text-sm">{suggestion.description}</p>}
                    {suggestion.schedule && <p className="text-sm"><strong>Zeitplan:</strong> {suggestion.schedule}</p>}
                    {suggestion.footer && (
                      <p className="text-sm" style={{ fontStyle: 'italic', color: 'var(--sec-fg-muted)' }}>
                        Footer: {suggestion.footer}
                      </p>
                    )}
                    <div className="card-footer">
                      <div className="btn-group">
                        <button onClick={() => handleSaveAiSuggestion(suggestion)} className="btn btn-success btn-sm">
                          Speichern
                        </button>
                        <button
                          onClick={() => {
                            setFormData(suggestion);
                            setShowForm(true);
                          }}
                          className="btn btn-primary btn-sm"
                        >
                          Bearbeiten & Speichern
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">{editingId ? 'Serienformat bearbeiten' : 'Neues Serienformat erstellen'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                Name (z.B. #TheArch) *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="z.B. #InnerGame"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Titel *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="z.B. Innere Stärke & Mindset"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Was ist der Fokus dieser Serie?"
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Fußzeile (Standard-Footer für Posts)
              </label>
              <textarea
                value={formData.footer}
                onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
                placeholder="z.B. '🔥 Was ist deine größte Herausforderung dabei?'"
                rows={3}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Zeitplan (z.B. "Dienstag 10:00")
              </label>
              <input
                type="text"
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                placeholder="z.B. Donnerstag, Freitag 18:00"
                className="form-input"
              />
            </div>

            <div className="btn-group">
              <button
                type="submit"
                className="btn btn-success"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div>
        <h2 className="form-section-title">Alle Serienformate ({seriesFormats.length})</h2>
        {seriesFormats.length === 0 ? (
          <EmptyState
            icon="layers"
            title="Noch keine Serienformate"
            description="Ein Serienformat ist ein wiederkehrender Beitragstyp (z. B. „Fehler der Woche“). Lass dir welche vorschlagen oder lege selbst eines an."
            action={{ label: 'KI-Vorschläge holen', onClick: () => { setShowAiSuggestion(true); loadStrategyContext(); } }}
            secondaryAction={{ label: 'Manuell anlegen', onClick: () => setShowForm(true) }}
          />
        ) : (
          <div className="grid-cards-2">
            {seriesFormats.map((format) => (
              <div
                key={format.id}
                className="card"
                data-focus-id={format.id}
              >
                <div className="card-header">
                  <div>
                    <h3 className="card-title">{format.name}</h3>
                    <p className="card-description"><strong>{format.title}</strong></p>
                  </div>
                </div>
                {format.description && <p className="text-sm">{format.description}</p>}
                {format.schedule && (
                  <p className="text-sm">
                    <strong>Zeitplan:</strong> {format.schedule}
                  </p>
                )}
                <RelatedLinks
                  className="mb-3"
                  label={`Verknüpft mit „${format.name}“`}
                  items={[
                    {
                      key: 'ideas',
                      label: format._count ? `Beitragsideen (${format._count.postIdeas})` : 'Beitragsideen',
                      icon: 'lightbulb',
                      to: `/post-ideas?seriesFormatId=${encodeURIComponent(format.id)}`,
                      done: !format._count || format._count.postIdeas > 0,
                      title: 'Alle Ideen dieses Formats',
                    },
                    {
                      key: 'workflow', label: 'Serien-Workflow', icon: 'workflow',
                      to: `/series-formats/${encodeURIComponent(format.id)}/workflow`, title: 'Ideen, Briefs und Texte für dieses Format erzeugen',
                    },
                  ]}
                />
                <div className="card-footer">
                  <div className="btn-group">
                    <button
                      onClick={() => navigate(`/series-formats/${format.id}/workflow`)}
                      className="btn btn-success btn-sm"
                    >
                      Workflow starten
                    </button>
                    <button
                      onClick={() => handleEdit(format)}
                      className="btn btn-primary btn-sm"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDelete(format.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesFormatsPage;
